import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Slider
 *
 * A styled range input with track, fill, and thumb.
 * Supports a gold accent variant and brand-consistent styling.
 *
 * Usage:
 *   <Slider label="Volume" value={0.7} onChange={setVolume} />
 *   <Slider variant="gold" min={0} max={100} value={50} onChange={setProgress} />
 */

export interface SliderProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size' | 'type'> {
	/** Label text */
	label?: string
	/** Helper text */
	hint?: string
	/** Current value */
	value?: number
	/** Change handler — receives the numeric value */
	onChange?: (value: number) => void
	/** Visual variant */
	variant?: 'default' | 'gold'
	/** Size */
	size?: 'sm' | 'md' | 'lg'
}

export const Slider = React.forwardRef<HTMLInputElement, SliderProps>(
	({ className, label, hint, value = 0, onChange, variant = 'default', size = 'md', min = 0, max = 100, step = 1, id, disabled, ...props }, ref) => {
		const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
		const dataSize = size

		const percent = (() => {
			const range = Number(max) - Number(min)
			if (range === 0) return 0
			return ((Number(value) - Number(min)) / range) * 100
		})()

		return (
			<div className={cn('jpg-slider flex flex-col gap-1.5', `jpg-slider--${dataSize}`, variant !== 'default' && `jpg-slider--${variant}`, disabled && 'jpg-slider--disabled', className)}>
				{label && (
					<label htmlFor={inputId} className="text-sm font-medium text-foreground font-ui">
						{label}
					</label>
				)}
				<div className="jpg-slider__track-wrapper">
					{/* Track background */}
					<div className="jpg-slider__track" />
					{/* Filled portion */}
					<div
						className={cn(
							'jpg-slider__fill',
							variant === 'gold' ? 'jpg-slider__fill--gold' : 'jpg-slider__fill--default',
						)}
						style={{ width: `${percent}%` }}
					/>
					{/* Native input */}
					<input
						ref={ref}
						id={inputId}
						type="range"
						min={min}
						max={max}
						step={step}
						value={value}
						disabled={disabled}
						className="jpg-slider__input"
						onChange={(e) => onChange?.(Number(e.target.value))}
						aria-valuemin={Number(min)}
						aria-valuemax={Number(max)}
						aria-valuenow={Number(value)}
						aria-valuetext={String(value)}
						aria-describedby={hint ? `${inputId}-hint` : undefined}
						{...props}
					/>
				</div>
				{hint && (
					<p id={`${inputId}-hint`} className="text-xs text-muted-foreground font-ui">
						{hint}
					</p>
				)}
			</div>
		)
	},
)

Slider.displayName = 'Slider'
